import { capitalize } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { SchemeForm, TrainingType } from '@strength/domain';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trpc } from '../../../../trpc/react';
import { Gallery } from '../../../components/gallery/gallery';
import { useViewer } from '../../../context/viewer';
import { useFilteredGalleryPagination } from '../../../hooks/useFilteredGalleryPagination';
import { useToast } from '../../../hooks/useToast';
import { CreateSchemeSheet } from './components/CreateSchemeSheet';
import { EditSchemeSheet } from './components/EditSchemeSheet';
import { SchemeCard } from './components/SchemeCard';
import { FocusOption, Scheme } from './types';
import { getSchemeFromForm } from './utils';

const columnHelper = createColumnHelper<Scheme>();

export function SchemesPage() {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const enableCreate = hasPermissions('library.schemes', 'create');
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [selectedFocuses, setSelectedFocuses] = useState<FocusOption[]>([]);
  const [selectedTrainingTypes, setSelectedTrainingTypes] = useState<
    TrainingType[]
  >([]);

  const {
    refetch,
    data,
    pageSize,
    setPageSize,
    page,
    setPage,
    viewMode,
    setViewMode,
    isLoading,
  } = useFilteredGalleryPagination({
    queryFn: trpc.schemeLibrary.searchSchemes.useQuery,
    queryInput: {
      searchTerm: debouncedSearchTerm,
      focusIds: selectedFocuses.map((f) => f.id),
      trainingTypeId: selectedTrainingTypes[0]?.id,
    },
  });

  const focusesQuery = trpc.schemeLibrary.trainingFocuses.useQuery(undefined, {
    staleTime: 10_000,
  });
  const focusOptions = useMemo(
    () => focusesQuery.data || [],
    [focusesQuery.data],
  );
  const trainingTypesQuery = trpc.trainingType.fetch.useQuery({});
  const trainingTypes = useMemo(
    () => trainingTypesQuery.data || [],
    [trainingTypesQuery.data],
  );

  const createScheme = trpc.schemeLibrary.createScheme.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const deleteScheme = trpc.schemeLibrary.deleteScheme.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const onDeleteScheme = (id: string) => {
    deleteScheme.mutate(
      { id },
      {
        onSuccess: () =>
          toast({
            title: t('scheme.deleted'),
            severity: 'success',
          }),
        onError: (error) => {
          toast({
            title: error.message,
            severity: 'error',
          });
        },
      },
    );
  };

  const onCreateScheme = (scheme: SchemeForm) => {
    createScheme.mutate(getSchemeFromForm(v4(), scheme), {
      onSuccess: () =>
        toast({
          title: t('scheme.created', { schemeName: scheme.name }),
          severity: 'success',
        }),
      onError: (error) => {
        toast({
          title: error.message,
          severity: 'error',
        });
      },
    });
  };

  const editScheme = trpc.schemeLibrary.editScheme.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const onEditScheme = (id: string, scheme: SchemeForm) => {
    editScheme.mutate(getSchemeFromForm(id, scheme), {
      onSuccess: () =>
        toast({
          title: t('scheme.edited', { schemeName: scheme.name }),
          severity: 'success',
        }),
      onError: (error) => {
        toast({
          title: error.message,
          severity: 'error',
        });
      },
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => t('name'),
        cell: ({ row }) => capitalize(row.original.name),
      }),
      columnHelper.display({
        id: 'focus',
        header: () => t('focus'),
        cell: ({ row }) => {
          const focusNames = row.original.focuses.map((f) => {
            const focus = focusOptions.find((fo) => fo.id === f.focusId);
            return focus ? focus.name : '';
          });
          return focusNames.map(capitalize).join(', ');
        },
      }),
    ],
    [t, focusOptions],
  );

  // Debounce the search term that goes to the query
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [],
  );

  // Handle immediate UI updates and trigger debounced query update
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearchTerm('');
    debouncedSetSearchTerm.clear();
    setSelectedFocuses([]);
    setSelectedTrainingTypes([]);
  }, [debouncedSetSearchTerm]);

  const filters = useMemo(
    () => [
      {
        id: 'focuses',
        type: 'select' as const,
        title: t('trainingFocus'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options: focusOptions.map((f) => ({
          id: f.id,
          label: f.name,
          value: f,
        })),
      },
      {
        id: 'trainingTypes',
        type: 'select' as const,
        title: t('exerciseType'),
        multiselect: false,
        options: trainingTypes.map((tt) => ({
          id: tt.id,
          label: tt.name,
          value: tt,
        })),
      },
    ],
    [t, focusOptions, trainingTypes],
  );

  const handleFilterChange = useCallback(
    (filters: Record<string, (FocusOption | TrainingType)[]>) => {
      const focusesFilter = filters['focuses'];
      if (focusesFilter) {
        setSelectedFocuses(focusesFilter as FocusOption[]);
      }
      const trainingTypesFilter = filters['trainingTypes'];
      if (trainingTypesFilter) {
        setSelectedTrainingTypes(trainingTypesFilter as TrainingType[]);
      }
    },
    [],
  );

  return (
    <Gallery<Scheme>
      title={t('schemes')}
      createButtonText={t('createScheme')}
      createDisabled={!enableCreate}
      createDisabledReason={t('permissionDisabled.create', {
        item: t('schemes'),
      })}
      data={data}
      columns={columns}
      filters={filters}
      viewMode={viewMode}
      isLoading={isLoading}
      setViewMode={setViewMode}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      renderCard={(item, viewMode, setSelected) => (
        <SchemeCard
          key={item.id}
          item={item}
          viewMode={viewMode}
          onClick={setSelected}
          focusOptions={focusOptions}
        />
      )}
      renderEditSheet={(selected, setSelected) => (
        <EditSchemeSheet
          scheme={selected}
          open={!!selected}
          focusOptions={focusOptions}
          trainingTypeOptions={trainingTypes}
          onOpenChange={() => setSelected(null)}
          onSubmit={onEditScheme}
          onDelete={onDeleteScheme}
        />
      )}
      renderCreateSheet={
        enableCreate
          ? (isOpen, setIsOpen) => (
              <CreateSchemeSheet
                open={isOpen}
                onOpenChange={setIsOpen}
                onSubmit={onCreateScheme}
                focusOptions={focusOptions}
                trainingTypeOptions={trainingTypes}
              />
            )
          : undefined
      }
    />
  );
}
